import { Radio, Space, Table, Modal, Button } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import Search from 'antd/lib/input/Search';
import { ColumnsType } from 'antd/lib/table';
import React, { FunctionComponent, useState } from 'react';
import { Granule } from '../../types/Granule';
import { deleteGranules } from '../../services/GranuleService';

type GranulesTableProps = {
  granules?: Granule[];
  onGranulesDeletion: () => void;
};

type GranulesTableFilter = 'ALL' | 'RELOADED' | 'NOT_RELOADED';

const filterOptions: Array<{ label: string; value: GranulesTableFilter }> = [
  { label: 'All', value: 'ALL' },
  { label: 'Reloaded', value: 'RELOADED' },
  { label: 'Not Reloaded', value: 'NOT_RELOADED' },
];

const granuleReloadedStatusFilter = (record: Granule, filter: GranulesTableFilter): boolean => {
  if (filter === 'ALL') return true;
  if (filter === 'RELOADED') return record.reloaded === true;
  if (filter === 'NOT_RELOADED') return record.reloaded !== true;
  return true;
};

const granuleSearchTextFilter = (record: Granule, searchText: string): boolean => {
  if (searchText === '') return true;
  return JSON.stringify(record).toLowerCase().includes(searchText);
};

export const GranulesTable: FunctionComponent<GranulesTableProps> = ({ granules, onGranulesDeletion }) => {
  const [filter, setFilter] = useState<GranulesTableFilter>('ALL');
  const [searchText, setSearchText] = useState<string>('');
  const [selectedKapsuleChildIds, setSelectedKapsuleChildIds] = useState<string[]>([]);
  if (!granules) return null;
  

  const columns: ColumnsType<Granule> = [
    { title: 'User', dataIndex: 'user',
      sorter: (a: Granule, b: Granule) => a.user.localeCompare(b.user)
    },    
    { title: 'ID', dataIndex: '_id',
      sorter: (a: Granule, b: Granule) => a._id.localeCompare(b._id)
    },
    { title: 'Key Value', dataIndex: 'key',
      sorter: (a: Granule, b: Granule) => {
        let akey = a.key;
        let bkey = b.key;
        if (akey === null) akey = ''
        if (bkey === null) bkey = ''
        return akey.toString().localeCompare(bkey.toString())
      }
    },
    { title: 'Last Update', dataIndex: 'updatedAt',
      sorter: (a: Granule, b: Granule) => {
        const aTime = new Date(a.updatedAt).getTime();
        const bTime = new Date(b.updatedAt).getTime();
        return aTime - bTime;
      }
    },
    {
      title: 'Action',
      dataIndex: '',
      key: 'x',
      render: (_text: string, record: Granule) => (
        <Space size="middle">
          <DeleteOutlined
            style={{ color: '#1890ff' }}
            onClick={() => {
              Modal.confirm({
                title: 'Confirm',
                content: `Are you sure to delete ${record._id}?`,
                okText: 'Yes',
                cancelText: 'Cancel',
                onOk: async () => {
                  await deleteGranules([record._id]);
                  onGranulesDeletion();
                },
              });
            }}
          />
        </Space>
      ),
    },
  ];

  const onSearch = (searchText: string) => {
    if (searchText) {
      setSearchText(searchText.toLowerCase());
    } else {
      setSearchText('');
    }
  };

  const handleFilterChange = (value?: GranulesTableFilter) => {
    if (value) {
      setFilter(value);
    }
  };

  const rowSelection = {
    onChange: (_selectedRowKeys: React.Key[], selectedRows: Granule[]) => {
      const selectedKeyIds = selectedRows.map((item) => item._id);
      setSelectedKapsuleChildIds(selectedKeyIds);
    }
  };

  return (
    <Table
      title={() => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <b>Granules</b>
          <div>
            <Radio.Group
              options={filterOptions}
              onChange={({ target: { value } }: { target: { value?: GranulesTableFilter } }) =>
                handleFilterChange(value)
              }
              value={filter}
              optionType="button"
              buttonStyle="solid"
            />
            <Button 
                disabled={selectedKapsuleChildIds.length === 0}
                type="primary"
                onClick={() => {
                  Modal.confirm({
                    title: 'Confirm',
                    content: `Are you sure to delete selected Kapsules?`,
                    okText: 'Yes',
                    cancelText: 'Cancel',
                    onOk: async () => {
                      /**执行批量删除 */
                      await deleteGranules(selectedKapsuleChildIds);
                      /**将缓存ids置为空 */
                      setSelectedKapsuleChildIds([]);
                      /**重新reload数据 */
                      onGranulesDeletion();
                    },
                  });
                }}
              >
              Delete Selection
            </Button>
            <Search placeholder="input search text" allowClear onSearch={onSearch} style={{ width: 200 }} />
          </div>
        </div>
      )}
      rowKey={(record) => record._id}
      columns={columns}
      rowSelection={{ ...rowSelection }}
      dataSource={granules
        .filter((item) => granuleReloadedStatusFilter(item, filter))
        .filter((item) => granuleSearchTextFilter(item, searchText))}
      expandable={{
        expandedRowRender: (record) => {
          const text = JSON.stringify(record.data, undefined, 4);
          return (
            <pre>
              <code>{text}</code>
            </pre>
          );
        },
        rowExpandable: (record) => !!record.data,
      }}
    />
  );
};
